// ---------------------------------------------------------------------------
// gifenc.js -- minimal GIF89a encoder, no dependencies.
//
// Three things keep the file small:
//   * one median-cut palette shared by every frame, built from a histogram of
//     all of them, so there are no per-frame local colour tables;
//   * inter-frame differencing -- pixels identical to the previous frame are
//     written as the transparent index and left to show through (disposal
//     method 1, "do not dispose");
//   * a per-frame dirty rectangle, so an animation where only a small part
//     moves encodes only that part.
// ---------------------------------------------------------------------------

(function () {

class Bytes {
  constructor() { this.a = new Uint8Array(1 << 16); this.n = 0; }
  _grow(k) {
    if (this.n + k <= this.a.length) return;
    let len = this.a.length;
    while (len < this.n + k) len *= 2;
    const b = new Uint8Array(len);
    b.set(this.a.subarray(0, this.n));
    this.a = b;
  }
  u8(v) { this._grow(1); this.a[this.n++] = v & 255; }
  u16(v) { this._grow(2); this.a[this.n++] = v & 255; this.a[this.n++] = (v >> 8) & 255; }
  raw(arr) { this._grow(arr.length); this.a.set(arr, this.n); this.n += arr.length; }
  str(s) { for (let i = 0; i < s.length; i++) this.u8(s.charCodeAt(i)); }
  done() { return this.a.subarray(0, this.n); }
}

// --- Palette: histogram at 5 bits per channel, then median cut -------------
function buildPalette(frames, maxColors, sampleStep) {
  const R = new Float64Array(32768), G = new Float64Array(32768);
  const B = new Float64Array(32768), N = new Float64Array(32768);
  const stride = 4 * sampleStep;
  for (const rgba of frames) {
    for (let p = 0; p < rgba.length; p += stride) {
      const r = rgba[p], g = rgba[p + 1], b = rgba[p + 2];
      const k = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
      R[k] += r; G[k] += g; B[k] += b; N[k]++;
    }
  }
  const keys = [];
  for (let k = 0; k < 32768; k++) if (N[k]) keys.push(k);
  if (!keys.length) return [[0, 0, 0]];

  const SHIFT = [10, 5, 0];
  let boxes = [keys];
  while (boxes.length < maxColors) {
    // Split whichever box holds the most pixels; that is what keeps gradients
    // smooth, since they are where the pixels actually are.
    let bi = -1, best = -1;
    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].length < 2) continue;
      let c = 0;
      for (const k of boxes[i]) c += N[k];
      if (c > best) { best = c; bi = i; }
    }
    if (bi < 0) break;

    const box = boxes[bi];
    let ch = 0, range = -1;
    for (let c = 0; c < 3; c++) {
      let lo = 99, hi = -1;
      for (const k of box) { const v = (k >> SHIFT[c]) & 31; if (v < lo) lo = v; if (v > hi) hi = v; }
      if (hi - lo > range) { range = hi - lo; ch = c; }
    }
    const sh = SHIFT[ch];
    box.sort((a, b) => (((a >> sh) & 31) - ((b >> sh) & 31)));

    let total = 0;
    for (const k of box) total += N[k];
    let acc = 0, cut = 1;
    for (let i = 0; i < box.length; i++) {
      acc += N[box[i]];
      if (acc >= total / 2) { cut = i + 1; break; }
    }
    cut = Math.max(1, Math.min(box.length - 1, cut));
    boxes.splice(bi, 1, box.slice(0, cut), box.slice(cut));
  }

  return boxes.map(box => {
    let r = 0, g = 0, b = 0, n = 0;
    for (const k of box) { r += R[k]; g += G[k]; b += B[k]; n += N[k]; }
    return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
  });
}

// Nearest-colour lookup, memoised per 5-bit cell.
function makeLookup(pal) {
  const cache = new Int16Array(32768).fill(-1);
  return (r, g, b) => {
    const k = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const hit = cache[k];
    if (hit >= 0) return hit;
    let best = 0, bd = Infinity;
    for (let i = 0; i < pal.length; i++) {
      const dr = r - pal[i][0], dg = g - pal[i][1], db = b - pal[i][2];
      const d = dr * dr + dg * dg + db * db;
      if (d < bd) { bd = d; best = i; }
    }
    cache[k] = best;
    return best;
  };
}

// --- LZW, GIF variant ------------------------------------------------------
function lzwEncode(indices, minCodeSize) {
  const out = new Bytes();
  const clearCode = 1 << minCodeSize;
  const eoiCode = clearCode + 1;
  let codeSize = minCodeSize + 1;
  let nextCode = eoiCode + 1;
  let table = new Map();
  let cur = 0, curBits = 0;

  const emit = (code) => {
    cur |= code << curBits;
    curBits += codeSize;
    while (curBits >= 8) { out.u8(cur & 255); cur >>>= 8; curBits -= 8; }
  };

  emit(clearCode);
  let prefix = indices[0];
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i];
    const key = (prefix << 8) | k;
    const found = table.get(key);
    if (found !== undefined) { prefix = found; continue; }
    emit(prefix);
    if (nextCode === 4096) {
      emit(clearCode);
      nextCode = eoiCode + 1;
      codeSize = minCodeSize + 1;
      table = new Map();
    } else {
      if (nextCode >= (1 << codeSize)) codeSize++;
      table.set(key, nextCode++);
    }
    prefix = k;
  }
  emit(prefix);
  emit(eoiCode);
  if (curBits > 0) out.u8(cur & 255);
  return out.done();
}

function writeSubBlocks(out, data) {
  for (let i = 0; i < data.length; i += 255) {
    const n = Math.min(255, data.length - i);
    out.u8(n);
    out.raw(data.subarray(i, i + n));
  }
  out.u8(0);
}

// --- Encode ----------------------------------------------------------------
// frames: array of Uint8ClampedArray RGBA buffers, all width*height*4.
// delays: array of per-frame delays in centiseconds.
function encode(frames, width, height, delays, opts) {
  opts = opts || {};
  const TRANSPARENT = 255;
  const pal = buildPalette(frames, 255, opts.sampleStep || 3);
  const lookup = makeLookup(pal);

  const out = new Bytes();
  out.str('GIF89a');
  out.u16(width); out.u16(height);
  out.u8(0xF7);           // global colour table, 256 entries
  out.u8(0); out.u8(0);
  for (let i = 0; i < 256; i++) {
    const p = pal[i] || [0, 0, 0];
    out.u8(p[0]); out.u8(p[1]); out.u8(p[2]);
  }
  // Loop forever.
  out.u8(0x21); out.u8(0xFF); out.u8(11); out.str('NETSCAPE2.0');
  out.u8(3); out.u8(1); out.u16(0); out.u8(0);

  const idx = new Uint8Array(width * height);
  let prev = null;

  for (let f = 0; f < frames.length; f++) {
    const rgba = frames[f];
    let minX = width, minY = height, maxX = -1, maxY = -1;

    for (let y = 0; y < height; y++) {
      const row = y * width;
      for (let x = 0; x < width; x++) {
        const p = (row + x) * 4;
        if (prev && prev[p] === rgba[p] && prev[p + 1] === rgba[p + 1]
                 && prev[p + 2] === rgba[p + 2]) {
          idx[row + x] = TRANSPARENT;
        } else {
          idx[row + x] = lookup(rgba[p], rgba[p + 1], rgba[p + 2]);
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    // A frame identical to the last still needs to exist, to hold its delay.
    if (maxX < 0) { minX = minY = 0; maxX = maxY = 0; idx[0] = TRANSPARENT; }

    const w = maxX - minX + 1, h = maxY - minY + 1;
    const sub = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      sub.set(idx.subarray((minY + y) * width + minX, (minY + y) * width + minX + w), y * w);
    }

    out.u8(0x21); out.u8(0xF9); out.u8(4);
    out.u8((1 << 2) | 1);            // disposal 1 (leave in place), transparency on
    out.u16(delays[f]);
    out.u8(TRANSPARENT); out.u8(0);

    out.u8(0x2C);
    out.u16(minX); out.u16(minY); out.u16(w); out.u16(h);
    out.u8(0);
    out.u8(8);
    writeSubBlocks(out, lzwEncode(sub, 8));

    prev = rgba;
  }

  out.u8(0x3B);
  return out.done();
}

window.GIFENC = { encode };
})();
